import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../contexts/TierContext';
import { supabase } from '@/lib/supabase';
import { useTranslation } from 'react-i18next';
import { authDebugLog } from '../utils/authDebugLog';
import { billingService } from '../services/billing';
import { getBillingSuccessTierPresentation } from '@/lib/billingTierSuccessPresentation';

export default function BillingSuccessPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { user, refreshSession, forceUserDataRefresh, isLoading: authLoading } = useAuth();
  const { refreshTierInfo: forceRefreshTierInfo } = useTier();
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [tierRefreshAttempts, setTierRefreshAttempts] = useState(0);
  const tierRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshSessionRef = useRef(refreshSession);
  const forceUserDataRefreshRef = useRef(forceUserDataRefresh);
  const forceRefreshTierInfoRef = useRef(forceRefreshTierInfo);
  const navigateRef = useRef(navigate);
  const tRef = useRef(t);

  refreshSessionRef.current = refreshSession;
  forceUserDataRefreshRef.current = forceUserDataRefresh;
  forceRefreshTierInfoRef.current = forceRefreshTierInfo;
  navigateRef.current = navigate;
  tRef.current = t;

  const userTier = user?.user_metadata?.tier || 'free';

  const sessionIdParam = searchParams.get('session_id');

  useEffect(() => {
    if (authLoading) {
      return;
    }

    setSessionId(sessionIdParam);

    let recoveryRedirectTimer: ReturnType<typeof setTimeout> | undefined;
    let tierRefreshTimer: ReturnType<typeof setTimeout> | undefined;

    const runPostCheckout = async () => {
      authDebugLog('BillingSuccess: post-checkout sequence start', {
        sessionIdParam,
        tierRefreshAttempts,
      });
      try {
        const {
          data: { session: bootSession },
          error: bootErr,
        } = await supabase.auth.getSession();
        authDebugLog('BillingSuccess: getSession', {
          hasSession: Boolean(bootSession),
          error: bootErr?.message,
        });

        if (!bootSession?.user) {
          authDebugLog(
            'BillingSuccess: no Supabase session after checkout — scheduling login redirect (3.5s)'
          );
          recoveryRedirectTimer = setTimeout(() => {
            const tr = tRef.current;
            toast.error(
              tr('billingSuccess.sessionRestore.toastTitle', { ns: 'pricing' }) as string,
              {
                description: tr('billingSuccess.sessionRestore.toastDescription', {
                  ns: 'pricing',
                }) as string,
                duration: 8000,
              }
            );
            navigateRef.current('/login?upgraded=true');
          }, 3500);
          setIsLoading(false);
          return;
        }

        if (sessionIdParam) {
          try {
            await billingService.confirmCheckoutSession(sessionIdParam);
            authDebugLog('BillingSuccess: confirmCheckoutSession completed');
            try {
              sessionStorage.removeItem('memara_pending_checkout_session_id');
            } catch {
              /* ignore */
            }
          } catch (e) {
            authDebugLog('BillingSuccess: confirmCheckoutSession failed', e);
          }
        }

        try {
          const sync = await billingService.syncSubscriptionFromStripe();
          authDebugLog('BillingSuccess: syncSubscriptionFromStripe', sync);
        } catch (e) {
          authDebugLog('BillingSuccess: syncSubscriptionFromStripe failed', e);
        }

        if (tierRefreshAttempts === 0) {
          try {
            await refreshSessionRef.current();
          } catch (e) {
            authDebugLog('BillingSuccess: refreshSession failed', e);
          }

          try {
            await forceUserDataRefreshRef.current();
          } catch (e) {
            authDebugLog('BillingSuccess: forceUserDataRefresh failed', e);
          }
        } else {
          try {
            await forceUserDataRefreshRef.current();
          } catch (e) {
            authDebugLog('BillingSuccess: forceUserDataRefresh (retry) failed', e);
          }
        }

        let apiTier: string | null = null;
        try {
          apiTier = await forceRefreshTierInfoRef.current();
        } catch (e) {
          authDebugLog('BillingSuccess: forceRefreshTierInfo failed', e);
        }

        const { data: { session: finalSession } } = await supabase.auth.getSession();
        const jwtTier = finalSession?.user?.user_metadata?.tier ?? 'free';
        const currentTier = apiTier ?? jwtTier;
        authDebugLog('BillingSuccess: tier after refresh', {
          apiTier,
          jwtTier,
          currentTier,
          tierRefreshAttempts,
        });

        if (currentTier === 'free' && tierRefreshAttempts < 1) {
          authDebugLog('BillingSuccess: scheduling tier retry in 5s (webhook latency)');
          if (tierRetryTimeoutRef.current) {
            clearTimeout(tierRetryTimeoutRef.current);
          }
          tierRetryTimeoutRef.current = setTimeout(() => {
            tierRetryTimeoutRef.current = null;
            setTierRefreshAttempts((prev) => prev + 1);
          }, 5000);
          return;
        }

        authDebugLog('BillingSuccess: sequence complete', { currentTier });
        localStorage.setItem('pending_tier_refresh', 'true');
        setIsLoading(false);
      } catch (error) {
        authDebugLog('BillingSuccess: post-checkout error', error);
        try {
          await forceRefreshTierInfoRef.current();
        } catch (tierError) {
          authDebugLog('BillingSuccess: tier refresh fallback failed', tierError);
        }
        setIsLoading(false);
      }
    };

    tierRefreshTimer = setTimeout(() => {
      void runPostCheckout();
    }, 1000);

    return () => {
      if (recoveryRedirectTimer) clearTimeout(recoveryRedirectTimer);
      if (tierRefreshTimer) clearTimeout(tierRefreshTimer);
      if (tierRetryTimeoutRef.current) {
        clearTimeout(tierRetryTimeoutRef.current);
        tierRetryTimeoutRef.current = null;
      }
    };
  }, [authLoading, sessionIdParam, tierRefreshAttempts]);

  const tierInfo = getBillingSuccessTierPresentation(userTier);
  const TierIcon = tierInfo.icon;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Processing your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6"
          >
            <CheckCircle className="w-10 h-10 text-green-600" />
          </motion.div>

          {/* Success Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('billingSuccess.welcome', { tier: tierInfo.name, ns: 'pricing' }) as string}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('billingSuccess.subscriptionActivated', { tier: tierInfo.name, ns: 'pricing' }) as string}
            </p>
          </motion.div>

          {/* Tier Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mb-8"
          >
            <Card className="border-2 border-indigo-200 bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center justify-center space-x-3">
                  <div className={`p-2 rounded-full ${tierInfo.bgColor}`}>
                    <TierIcon className={`w-6 h-6 ${tierInfo.color}`} />
                  </div>
                  <span className="text-2xl font-bold">{t('billingSuccess.planActivated', { tier: tierInfo.name, ns: 'pricing' }) as string}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tierInfo.features.map((feature, index) => (
                    <div key={index} className="flex items-center justify-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="space-y-4"
          >
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">{t('billingSuccess.whatsNext', { ns: 'pricing' }) as string}</h2>
            
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="text-left">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                    <h3 className="text-lg font-semibold">{t('billingSuccess.enterWorkspace.title', { ns: 'pricing' }) as string}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {t('billingSuccess.enterWorkspace.description', { ns: 'pricing' }) as string}
                  </p>
                  <Button
                    onClick={() => navigate('/spaces')}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {t('billingSuccess.enterWorkspace.button', { ns: 'pricing' }) as string}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              <Card className="text-left">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <Sparkles className="w-6 h-6 text-indigo-600" />
                    <h3 className="text-lg font-semibold">{t('billingSuccess.explorePlan.title', { ns: 'pricing' }) as string}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {t('billingSuccess.dashboardDescription', { ns: 'pricing' }) as string}
                  </p>
                  <Button
                    onClick={() => {
                      // Ensure tier refresh flag is set before navigating
                      localStorage.setItem('pending_tier_refresh', 'true');
                      navigate('/dashboard?upgraded=true');
                    }}
                    variant="outline"
                    className="w-full border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                  >
                    {t('billingSuccess.viewDashboard', { ns: 'pricing' }) as string}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Payment Details */}
          {sessionId && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="mt-8 p-4 bg-gray-50 rounded-lg"
            >
              <p className="text-sm text-gray-600">
                {t('billingSuccess.paymentConfirmation', { ns: 'pricing' }) as string}: <span className="font-mono text-xs">{sessionId}</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {t('billingSuccess.receiptSent', { ns: 'pricing' }) as string}
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
} 